/**
 * Геометрия контуров для кадров Astra — с доклеенными атрибутами среза.
 *
 * Перенесено из прототипа (`prototype/app/src/map/useContourGeo.ts`) с одним
 * изменением: откуда брать ПОЛИГОНЫ, решает источник маршрута
 * (`data-source/routes.ts`), как и для всех остальных данных экрана.
 *
 * * `api` — канонические выборки `/map/reference` и `/map/contours` (те же,
 *   что у канонического `MapCanvas`) одним запросом на рамку района;
 *   идентификатор фичи — `String(contour_id)`, ровно тот, которым адаптеры
 *   `data-source/api/*` называют контур (`contours.meta.json`, обязательства);
 * * `fixtures` — `prototype/data/geo/contours.geo.json`, как в прототипе.
 *
 * Атрибуты (`no`, `massif_id`, `farm_id`, площадь, категория, культура,
 * статус геометрии) доклеиваются из `contours.meta.json` того же источника.
 * Джойн делается один раз на загрузке: дальше карта живёт выражениями и
 * feature-state, без React на каждую фичу.
 */

import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { api, type MapContours, type MapReference } from '../../api/client';
import { VersionedMemo } from '../../api/dataEpoch';
import { onPrincipalChange, principalEpoch, subscribePrincipal } from '../../api/session';
import { useAnalysisContext } from '../../analysis/context';
import {
  DISTRICT_FALLBACK_BBOX,
  geoBounds,
  hasAcceptedGeometry,
  truncationNotice,
  type Bbox,
} from '../../features/map/mapModel';
import { FILES, GEO_CONTOURS, GEO_CONTOURS_STUB } from '../data/loader';
import { useJsonFile, useTrackData, type DataSource, type DataState } from '../data/useTrackData';
import type { ContourFeatureCollection, ContourMeta } from '../data/types';
import { useDataSource } from '../data-source';
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';

const m = defineMessages({
  layerFailed: {
    id: 'map.geo.layerFailed',
    defaultMessage: 'Слой карты не загрузился: сервер не ответил',
    description: 'Причина, почему карта не собралась: сервер не ответил (показывается в пояснении на месте карты)',
  },
  noReason: {
    id: 'map.geo.noReason',
    defaultMessage: 'причина не названа',
    description: 'Причина отсутствия геометрии, когда её никто не назвал (строчная фраза)',
  },
  geoUnavailable: {
    id: 'map.geo.geoUnavailable',
    defaultMessage: 'геометрия недоступна: {reason}',
    description: 'Причина пустой карты (строчная фраза); {reason} — причина',
  },
  attributesOff: {
    id: 'map.geo.attributesOff',
    defaultMessage: 'атрибуты среза не подключены: {reason}',
    description: 'Оговорка к карте, когда атрибуты контуров (хозяйство, культура) не подгрузились (строчная фраза); {reason} — причина',
  },
});


export interface ContourGeo {
  state: DataState;
  reason: string | null;
  /** Геометрия с доклеенными атрибутами выбранного среза. */
  geo: ContourFeatureCollection | null;
  byId: Map<string, ContourMeta>;
  source: DataSource | null;
  /** Сколько фич не нашли своей строки в contours.meta.json. */
  unmatched: number;
  /** Откуда полигоны: канон или фикстуры прототипа. */
  origin: 'api' | 'fixtures';
  /** Рамка района — первый кадр камеры, если фокуса нет. */
  districtBbox: Bbox;
  /** Контекстные слои канона (рамка района, махалли); у фикстур их нет. */
  district: ContourFeatureCollection;
  mahallas: ContourFeatureCollection;
  /** Выдача усечена лимитом сервера — сказать словами. */
  truncated: string | null;
}

const EMPTY_META = new Map<string, ContourMeta>();
const EMPTY_FC: ContourFeatureCollection = { type: 'FeatureCollection', features: [] };

/**
 * Зум выдачи. Кадры Astra подвижны (панорама и масштаб), а район грузится
 * ОДНИМ запросом, поэтому геометрия берётся с упрощением зума 13: весь район
 * (≈4,6 тыс. фич) проходит в лимит 5000 без усечения, а при приближении
 * полигоны не рассыпаются на грубые многоугольники обзорного зума.
 */
const ASTRA_ZOOM = 13;

type CanonGeo =
  | { state: 'loading' }
  | { state: 'missing'; reason: string }
  | { state: 'ready'; reference: MapReference; contours: MapContours };

/**
 * Одна выдача на контекст анализа на всё приложение: карта стоит на десятке
 * экранов, и перезапрашивать 4 МБ полигонов на каждом переходе незачем.
 * Неудача не кэшируется — следующий кадр попробует снова.
 */
const canonCache = new VersionedMemo<CanonGeo>();

// Сервер режет `/map/contours` областью чтения: фермеру — его земля, району —
// весь район. Сменился аккаунт — прежняя геометрия не его (ни больше, ни
// меньше, чем ему положено).
onPrincipalChange(() => canonCache.clear());


function loadCanonGeo(key: string, params: Record<string, unknown>): Promise<CanonGeo> {
  // Под версией данных (agroai-0e8.26, журнал №1638): подписи полигонов
  // меняются записью размещения, и на следующей навигации геометрия
  // отдаётся из памяти, только если сервер снова подтвердил ту же версию.
  return canonCache.get(key, async (): Promise<CanonGeo> => {
    try {
      const reference = await api.mapReference({ ...params, zoom: ASTRA_ZOOM } as never);
      const bbox = geoBounds(reference.district.features) ?? DISTRICT_FALLBACK_BBOX;
      const contours = await api.mapContours({
        ...params,
        bbox: bbox.join(','),
        zoom: ASTRA_ZOOM,
      } as never);
      return { state: 'ready', reference, contours };
    } catch (cause) {
      canonCache.delete(key);
      // Техническая причина — в консоль, на экране — слова для хокима (О6).
      console.warn('слой карты не загрузился', cause);
      return {
        state: 'missing',
        reason: tr(m.layerFailed),
      };
    }
  });
}

function useCanonGeo(enabled: boolean): CanonGeo {
  const context = useAnalysisContext();
  const epoch = useSyncExternalStore(subscribePrincipal, principalEpoch, principalEpoch);
  const [value, setValue] = useState<CanonGeo>({ state: 'loading' });
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setValue({ state: 'loading' });
    void loadCanonGeo(context.key, context.params as Record<string, unknown>).then((next) => {
      if (alive) setValue(next);
    });
    return () => {
      alive = false;
    };
  }, [enabled, context.key, context.params, epoch]);
  return value;
}

/** Канонические фичи → форма фичи Astra: `id` строкой, атрибуты канона при ней. */
function canonFeatures(
  features: ReadonlyArray<{ id: number; geometry: { [key: string]: unknown } | null; properties: object }>,
): ContourFeatureCollection {
  return {
    type: 'FeatureCollection',
    features: features.filter(hasAcceptedGeometry).map((feature) => {
      const props = feature.properties as {
        contour_id?: number;
        contour_no?: string;
        crop_demo?: string | null;
      };
      const id = String(props.contour_id ?? feature.id);
      return {
        type: 'Feature',
        geometry: feature.geometry as ContourFeatureCollection['features'][number]['geometry'],
        properties: {
          ...(feature.properties as Record<string, unknown>),
          id,
          no: props.contour_no ?? id,
          // Культура сезона на каноне приходит с самой фичей карты — размещение
          // демо-слоя (`crop_demo`); реестр контуров культуры не несёт. При
          // выключенном демо поле пусто, и слой культур честно отказывает.
          crop_2026: props.crop_demo ?? null,
        },
      };
    }),
  };
}

function referenceFeatures(
  features: ReadonlyArray<{ id: number; geometry: { [key: string]: unknown } | null; properties: object }>,
): ContourFeatureCollection {
  return {
    type: 'FeatureCollection',
    features: features.filter(hasAcceptedGeometry).map((feature) => ({
      type: 'Feature',
      id: feature.id,
      geometry: feature.geometry as ContourFeatureCollection['features'][number]['geometry'],
      properties: { ...(feature.properties as Record<string, unknown>), id: String(feature.id), no: '' },
    })),
  };
}

export function useContourGeo(): ContourGeo {
  const origin = useDataSource().current.kind;
  const canon = useCanonGeo(origin === 'api');
  const geoFile = useJsonFile<ContourFeatureCollection>(
    origin === 'fixtures' ? GEO_CONTOURS : null,
    GEO_CONTOURS_STUB,
  );
  const metaFile = useTrackData<ContourMeta[]>(FILES.contours);

  return useMemo<ContourGeo>(() => {
    const base = {
      origin,
      byId: EMPTY_META,
      source: null,
      unmatched: 0,
      geo: null,
      districtBbox: DISTRICT_FALLBACK_BBOX,
      district: EMPTY_FC,
      mahallas: EMPTY_FC,
      truncated: null,
    } as const;

    const polygons =
      origin === 'api'
        ? canon.state === 'ready'
          ? { state: 'ready' as const, data: canonFeatures(canon.contours.features), source: 'track' as DataSource }
          : canon.state === 'missing'
            ? { state: 'missing' as const, reason: canon.reason }
            : { state: 'loading' as const }
        : geoFile.state === 'ready' && geoFile.data
          ? { state: 'ready' as const, data: geoFile.data, source: geoFile.source }
          : geoFile.state === 'missing'
            ? { state: 'missing' as const, reason: geoFile.reason ?? tr(m.noReason) }
            : { state: 'loading' as const };

    if (polygons.state === 'loading' || metaFile.state === 'loading') {
      return { ...base, state: 'loading', reason: null };
    }
    if (polygons.state === 'missing') {
      return { ...base, state: 'missing', reason: tr(m.geoUnavailable, { reason: polygons.reason }) };
    }

    const byId = new Map<string, ContourMeta>();
    for (const row of metaFile.data ?? []) byId.set(row.id, row);

    let unmatched = 0;
    const features = polygons.data.features.map((feature) => {
      const id = String(feature.properties?.id ?? '');
      const meta = byId.get(id);
      if (!meta) {
        unmatched += 1;
        return feature;
      }
      return {
        ...feature,
        properties: {
          ...feature.properties,
          id,
          no: meta.no,
          massif_id: meta.massif_id,
          farm_id: meta.farm_id,
          area_ha: meta.area_stated === false ? null : meta.area_ha,
          category: meta.category,
          crop_2026: meta.crop_2026 ?? (feature.properties?.crop_2026 as string | null | undefined) ?? null,
          crop_2025w: meta.crop_2025w,
          geometry_status: meta.geometry,
        },
      };
    });
    const geo: ContourFeatureCollection = { type: 'FeatureCollection', features };

    const district =
      origin === 'api' && canon.state === 'ready'
        ? referenceFeatures(canon.reference.district.features)
        : EMPTY_FC;
    const mahallas =
      origin === 'api' && canon.state === 'ready'
        ? referenceFeatures(canon.reference.mahallas.features)
        : EMPTY_FC;
    const truncated =
      origin === 'api' && canon.state === 'ready'
        ? (truncationNotice(canon.contours)?.text ?? null)
        : null;

    return {
      ...base,
      state: 'ready',
      reason:
        metaFile.state === 'missing'
          ? tr(m.attributesOff, { reason: metaFile.reason ?? tr(m.noReason) })
          : null,
      geo,
      byId,
      source: polygons.source,
      unmatched,
      districtBbox:
        geoBounds(
          (district.features.length > 0 ? district.features : features) as unknown as {
            geometry: { [key: string]: unknown } | null;
          }[],
        ) ??
        DISTRICT_FALLBACK_BBOX,
      district,
      mahallas,
      truncated,
    };
  }, [origin, canon, geoFile.state, geoFile.data, geoFile.reason, geoFile.source, metaFile.state, metaFile.data, metaFile.reason]);
}
